import { useCallback, useEffect, useState } from 'react';

import { getAllAlerts, getMyAlerts } from '../../services/alerts/alertsService';
import { canSeeAlerts, isAdminUser } from './alertLabels';

const REFRESH_MS = 10 * 60 * 1000; // las alertas se evalúan cada 30 min
const ON_OPEN_REFRESH_MS = 60 * 1000; // al abrir la campana se acepta una copia de hasta 1 minuto
const CHECK_MS = 60 * 1000;        // cada pestaña revisa la copia local cada minuto (sin pedir al servidor)
const RETRY_AFTER_ERROR_MS = 60 * 1000;
const LOCK_NAME = 'agroup-open-alerts-fetch';
const MAX_ITEMS = 10;

const storageKey = (email) => `open-alerts:${email}`;

const readCache = (key) => {
    try {
        return JSON.parse(localStorage.getItem(key)) || null;
    } catch {
        return null;
    }
};

const writeCache = (key, value) => localStorage.setItem(key, JSON.stringify(value));

const needsFetch = (cache, maxAgeMs) => {
    const now = Date.now();
    if (cache?.failedAt && now - cache.failedAt < RETRY_AFTER_ERROR_MS) return false;
    return !cache?.fetchedAt || now - cache.fetchedAt >= maxAgeMs;
};

/**
 * Pide las alertas activas solo si la copia compartida está vencida. Con varias
 * pestañas abiertas, el lock del navegador (Web Locks API) deja pasar solo a
 * una; las demás se actualizan con el evento 'storage' cuando esa escribe.
 */
const fetchIfNeeded = async (key, isAdmin, maxAgeMs) => {
    const run = async () => {
        const cache = readCache(key);
        if (!needsFetch(cache, maxAgeMs)) return; // otra pestaña ya lo actualizó
        try {
            const params = { status: 'OPEN', limit: MAX_ITEMS, page: 1 };
            const data = isAdmin ? await getAllAlerts(params) : await getMyAlerts(params);
            writeCache(key, { fetchedAt: Date.now(), alerts: data.content, total: data.totalElements });
        } catch (error) {
            // Se conserva lo último conocido y se espera un minuto antes de reintentar
            writeCache(key, { ...cache, failedAt: Date.now() });
            console.error('Error cargando alertas activas: ', error);
        }
    };

    if (navigator.locks) {
        await navigator.locks.request(LOCK_NAME, { ifAvailable: true }, async (lock) => {
            if (lock) await run();
        });
    } else {
        await run();
    }
};

/**
 * Alertas activas del usuario (o de todos los sensores si es administrador),
 * compartidas entre todas las pestañas abiertas.
 */
const useOpenAlerts = (user) => {
    const enabled = canSeeAlerts(user);
    const isAdmin = isAdminUser(user);
    const key = user?.email ? storageKey(user.email) : null;
    const [state, setState] = useState({ alerts: [], total: 0 });

    const sync = useCallback(() => {
        const cache = key ? readCache(key) : null;
        if (cache?.alerts) setState({ alerts: cache.alerts, total: cache.total });
    }, [key]);

    /** Al abrir la campana: refresca si la copia compartida tiene más de 1 minuto. */
    const refresh = useCallback(async () => {
        if (!enabled || !key) return;
        await fetchIfNeeded(key, isAdmin, ON_OPEN_REFRESH_MS);
        sync();
    }, [enabled, key, isAdmin, sync]);

    useEffect(() => {
        if (!enabled || !key) return undefined;

        const tick = async () => {
            await fetchIfNeeded(key, isAdmin, REFRESH_MS);
            sync();
        };
        const onStorage = (event) => {
            if (event.key === key) sync();
        };

        sync();
        tick();
        const interval = setInterval(tick, CHECK_MS);
        window.addEventListener('storage', onStorage);
        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', onStorage);
        };
    }, [enabled, key, isAdmin, sync]);

    return { ...state, enabled, isAdmin, refresh };
};

export default useOpenAlerts;
